import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Grid, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableFooter, TableHead, TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import {
  Check as CheckIcon, Close as CloseIcon, KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import MenuService from '../../services/menu.service';
import { apiError, formatPrice } from './menuUtils';

const STATUS_COLOR = { pending: 'warning', confirmed: 'success', cancelled: 'default' };

// Local calendar day as Y-m-d (the API reads these as the restaurant's local days).
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return ymd(d); };
const PRESETS = {
  today: () => [daysAgo(0), daysAgo(0)],
  yesterday: () => [daysAgo(1), daysAgo(1)],
  week: () => [daysAgo(6), daysAgo(0)],
  month: () => [daysAgo(29), daysAgo(0)],
};

const Stat = ({ label, value, hint, color }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: '100%' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h5" fontWeight={700} color={color} sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
  </Paper>
);

const OrderRow = ({ order, money, busy, onConfirm, onCancel, t }) => {
  const [open, setOpen] = useState(false);
  const pending = order.status === 'pending';
  const created = new Date(order.created_at);
  const summary = order.items.map((i) => `${i.quantity} × ${i.name}${i.option_label ? ` (${i.option_label})` : ''}`).join(', ');

  return (
    <>
      <TableRow hover sx={{ '& > td': { borderBottom: open ? 0 : undefined }, bgcolor: pending ? 'action.hover' : undefined }}>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen(!open)} aria-label={t('menus.orderDetail')}>
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell><Typography fontWeight={700}>#{order.number}</Typography></TableCell>
        <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {order.customer_name || <Typography component="span" color="text.disabled">—</Typography>}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {created.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} · {created.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
        </TableCell>
        <TableCell sx={{ maxWidth: 360 }}>
          <Typography variant="body2" noWrap title={summary}>{summary}</Typography>
        </TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{money(order.total)}</TableCell>
        <TableCell><Chip size="small" color={STATUS_COLOR[order.status]} label={t(`menus.orderStatus.${order.status}`)} /></TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {pending ? (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />} disabled={busy} onClick={() => onConfirm(order)}>
                {t('menus.confirmOrder')}
              </Button>
              <Button size="small" color="error" startIcon={<CloseIcon />} disabled={busy} onClick={() => onCancel(order)}>
                {t('menus.cancelOrder')}
              </Button>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">{order.handler?.name || ''}</Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, ...(open ? {} : { borderBottom: 0 }) }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 2, pl: { md: 7 } }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('menus.product')}</TableCell>
                    <TableCell align="right">{t('menus.quantity')}</TableCell>
                    <TableCell align="right">{t('menus.unitPrice')}</TableCell>
                    <TableCell align="right">{t('menus.amount')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell>{i.name}{i.option_label && <Typography component="span" color="text.secondary"> · {i.option_label}</Typography>}</TableCell>
                      <TableCell align="right">{i.quantity}</TableCell>
                      <TableCell align="right">{money(i.unit_price)}</TableCell>
                      <TableCell align="right">{money(i.line_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={3}><Typography fontWeight={700}>{t('menus.orderTotal')}</Typography></TableCell>
                    <TableCell align="right"><Typography fontWeight={700}>{money(order.total)}</Typography></TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
              {order.status !== 'pending' && (
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  {t(order.status === 'confirmed' ? 'menus.confirmedBy' : 'menus.cancelledBy', {
                    name: order.handler?.name || '—',
                    time: new Date(order.confirmed_at || order.cancelled_at).toLocaleString(),
                  })}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/**
 * Orders diners sent with "Pedir por WhatsApp": confirm / cancel the pending
 * ones, and a report for the chosen days (orders by status, products sold).
 */
const MenuOrders = ({ menu, onPendingTotal, notify, t }) => {
  const [preset, setPreset] = useState('today');
  const [range, setRange] = useState(PRESETS.today());
  const [status, setStatus] = useState('');
  const [view, setView] = useState('orders');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [orders, setOrders] = useState({ data: [], total: 0 });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const money = (v) => formatPrice(menu.currency_symbol, v);
  const [from, to] = range;

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const params = { from, to };
      const [list, rep] = await Promise.all([
        MenuService.getOrders(menu.uuid, { ...params, status: status || undefined, page: page + 1, per_page: perPage }),
        MenuService.getOrdersReport(menu.uuid, params),
      ]);
      setOrders({ data: list.data.data, total: list.data.total });
      setReport(rep.data.data);
      onPendingTotal(rep.data.data.pending_total);
    } catch (error) {
      notify(apiError(error, t('menus.errorLoadingOrders')), 'error');
    } finally {
      setLoading(false);
    }
  }, [menu.uuid, from, to, status, page, perPage, onPendingTotal, notify, t]);

  useEffect(() => { load(); }, [load]);

  // New orders arrive while the restaurant is open: refresh quietly when today is in the range.
  useEffect(() => {
    if (to < daysAgo(0)) return undefined;
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  }, [load, to]);

  const pickPreset = (p) => {
    if (!p) return;
    setPreset(p);
    if (PRESETS[p]) setRange(PRESETS[p]());
    setPage(0);
  };

  const setDay = (which, value) => {
    if (!value) return;
    setPreset('custom');
    setPage(0);
    setRange(([f, tt]) => {
      const next = which === 'from' ? [value, tt] : [f, value];
      return next[0] > next[1] ? [value, value] : next;
    });
  };

  // Pending orders from before the range: widen it back to the oldest one and list only pending.
  const showOlderPending = () => {
    setPreset('custom');
    setRange([report.oldest_pending, to]);
    setStatus('pending');
    setView('orders');
    setPage(0);
  };

  const act = async (order, action) => {
    if (action === 'cancel' && !window.confirm(t('menus.cancelOrderConfirm', { number: order.number }))) return;
    setBusy(true);
    try {
      const res = action === 'confirm'
        ? await MenuService.confirmOrder(menu.uuid, order.uuid)
        : await MenuService.cancelOrder(menu.uuid, order.uuid);
      notify(res.data.message);
    } catch (error) {
      notify(apiError(error, t('menus.errorSaving')), 'error');
    } finally {
      setBusy(false);
      load(true);
    }
  };

  const s = report?.summary;
  const productsTotal = (report?.products || []).reduce((acc, p) => ({ qty: acc.qty + p.quantity, amount: acc.amount + p.amount }), { qty: 0, amount: 0 });

  return (
    <Stack spacing={3}>
      {/* Range */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <ToggleButtonGroup size="small" exclusive value={preset} onChange={(_, v) => pickPreset(v)} sx={{ flexWrap: 'wrap' }}>
            {['today', 'yesterday', 'week', 'month', 'custom'].map((p) => (
              <ToggleButton key={p} value={p}>{t(`menus.range.${p}`)}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
            <TextField size="small" type="date" label={t('menus.rangeFrom')} value={from} onChange={(e) => setDay('from', e.target.value)}
              InputLabelProps={{ shrink: true }} inputProps={{ max: daysAgo(0) }} />
            <TextField size="small" type="date" label={t('menus.rangeTo')} value={to} onChange={(e) => setDay('to', e.target.value)}
              InputLabelProps={{ shrink: true }} inputProps={{ max: daysAgo(0) }} />
          </Stack>
          <Button startIcon={<RefreshIcon />} onClick={() => load()} disabled={loading}>{t('menus.refresh')}</Button>
        </Stack>
      </Paper>

      {!menu.whatsapp_orders && (
        <Typography variant="body2" color="text.secondary">{t('menus.ordersOffHint')}</Typography>
      )}

      {report?.pending_before > 0 && (
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={showOlderPending}>{t('menus.showPending')}</Button>}>
          {t(report.pending_before === 1 ? 'menus.pendingBeforeOne' : 'menus.pendingBefore', { count: report.pending_before })}
        </Alert>
      )}

      {/* Summary */}
      {s && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Stat label={t('menus.statOrders')} value={s.orders} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Stat label={t('menus.orderStatus.pending')} value={s.pending.orders} hint={money(s.pending.amount)} color={s.pending.orders ? 'warning.main' : undefined} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Stat label={t('menus.statSold')} value={money(s.confirmed.amount)}
              hint={t('menus.statSoldHint', { count: s.confirmed.orders, avg: money(s.average_ticket) })} color="success.main" />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Stat label={t('menus.orderStatus.cancelled')} value={s.cancelled.orders} hint={money(s.cancelled.amount)} />
          </Grid>
        </Grid>
      )}

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="orders">{t('menus.viewOrders')}</ToggleButton>
          <ToggleButton value="products">{t('menus.viewProducts')}</ToggleButton>
        </ToggleButtonGroup>
        {view === 'orders' && (
          <Stack direction="row" spacing={1}>
            {['', 'pending', 'confirmed', 'cancelled'].map((st) => (
              <Chip key={st || 'all'} label={st ? t(`menus.orderStatus.${st}`) : t('menus.allStatuses')} clickable
                color={status === st ? 'primary' : 'default'} variant={status === st ? 'filled' : 'outlined'}
                onClick={() => { setStatus(st); setPage(0); }} />
            ))}
          </Stack>
        )}
        {loading && <CircularProgress size={20} />}
      </Stack>

      {view === 'orders' ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>{t('menus.orderNumber')}</TableCell>
                  <TableCell>{t('menus.customer')}</TableCell>
                  <TableCell>{t('menus.orderTime')}</TableCell>
                  <TableCell>{t('menus.orderItems')}</TableCell>
                  <TableCell align="right">{t('menus.orderTotal')}</TableCell>
                  <TableCell>{t('menus.status')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.data.map((o) => (
                  <OrderRow key={o.uuid} order={o} money={money} busy={busy} t={t}
                    onConfirm={(ord) => act(ord, 'confirm')} onCancel={(ord) => act(ord, 'cancel')} />
                ))}
                {!loading && orders.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>{t('menus.noOrders')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={orders.total} page={page} rowsPerPage={perPage} rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setPerPage(+e.target.value); setPage(0); }}
            labelRowsPerPage={t('menus.rowsPerPage')} />
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 2 }}>{t('menus.productsHint')}</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('menus.product')}</TableCell>
                  <TableCell align="right">{t('menus.timesSold')}</TableCell>
                  <TableCell align="right">{t('menus.inOrders')}</TableCell>
                  <TableCell align="right">{t('menus.amount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(report?.products || []).map((p) => (
                  <TableRow key={`${p.name}|${p.option_label || ''}`} hover>
                    <TableCell>{p.name}{p.option_label && <Typography component="span" color="text.secondary"> · {p.option_label}</Typography>}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{p.quantity}</TableCell>
                    <TableCell align="right">{p.orders}</TableCell>
                    <TableCell align="right">{money(p.amount)}</TableCell>
                  </TableRow>
                ))}
                {!loading && !report?.products?.length && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>{t('menus.noProductsSold')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
              {report?.products?.length > 0 && (
                <TableFooter>
                  <TableRow>
                    <TableCell><Typography fontWeight={700}>{t('menus.orderTotal')}</Typography></TableCell>
                    <TableCell align="right"><Typography fontWeight={700}>{productsTotal.qty}</Typography></TableCell>
                    <TableCell />
                    <TableCell align="right"><Typography fontWeight={700}>{money(productsTotal.amount)}</Typography></TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Stack>
  );
};

export default MenuOrders;
